import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Input } from "../../../../components/ui/input";
import { api } from "../../../../lib/api";

/**
 * Searchable Member / Party field - a free-text input backed by a debounced
 * (250ms) lookup against the existing Owner Directory
 * (/society/directory?q=...), which already searches Member/Owner Name,
 * Flat/Unit (wing+flatNumber), Tenant Name and Mobile server-side - so the
 * user can find "Bharat Shah — Flat A-204" by typing any of those without
 * scrolling a long list, per the Phase 5 "Searchable Selectors" correction.
 * Free text is still accepted (and required to remain accepted) since Phase
 * 5 explicitly keeps member linkage manual/reference-only until Phase 6
 * connects real Billing - this is a convenience, not a foreign key.
 */
export default function PartySearchInput({ value, onChange, placeholder = "Search member or flat..." }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const search = (text) => {
    clearTimeout(timer.current);
    if (!text || text.trim().length < 2) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/society/directory?q=${encodeURIComponent(text.trim())}&activeOnly=true`);
        setSuggestions((Array.isArray(res.data) ? res.data : []).slice(0, 8));
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const handleChange = (text) => {
    setQuery(text);
    onChange(text);
    search(text);
    setOpen(true);
  };

  const pick = (row) => {
    const flat = [row.wing, row.flatNumber].filter(Boolean).join("-");
    const label = row.ownerName ? `${row.ownerName} — Flat ${flat}` : flat;
    setQuery(label);
    onChange(label);
    setOpen(false);
    setSuggestions([]);
  };

  const clear = () => {
    setQuery("");
    onChange("");
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className={query ? "pr-8" : undefined}
        />
        {query && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827]"
            title="Clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-sm border border-[#E5E7EB] bg-white shadow-md">
          {suggestions.map((row) => (
            <button
              key={row.id}
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-[#F9FAFB]"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(row)}
            >
              <span className="font-semibold text-[#111827]">{row.ownerName || "-"}</span>
              <span className="ml-2 text-[#6B7280]">Flat {[row.wing, row.flatNumber].filter(Boolean).join("-")}{row.ownerMobile ? ` · ${row.ownerMobile}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
